/**
 * The words on the delivery reports, generated from the numbers.
 *
 * Deterministic templates, no model in the loop: the same data always reads
 * the same way. Each finding only appears when its condition holds, so the
 * overview tells what's notable this period instead of filling slots.
 * Covered by story.test.ts.
 */
import type {
  CompareRow,
  DeliveryPace,
  DeliveryReport,
  DeliverySeries,
  WindowStats,
} from "@aura/shared";
import { changeText, fmtInt, fmtNum, fmtPct, joinList, plural } from "~/lib/format";

/** "+12", "-3", "0". */
export function signed(n: number): string {
  return n > 0 ? `+${fmtInt(n)}` : fmtInt(n);
}

/** "+12%", "-3%", "new" when the previous period had none, "" when both are 0. */
export function signedPct(row: Pick<CompareRow, "changePct" | "current">): string {
  if (row.changePct === null) return row.current.merged > 0 ? "new" : "";
  return row.changePct > 0 ? `+${row.changePct}%` : `${row.changePct}%`;
}

/** Ledger change for a percentage: "+4 pts" ▲, read as points, never a percent of a percent. */
export function pointsChange(current: number | null, prior: number | null) {
  return changeText(
    current === null ? null : Math.round(current),
    prior === null ? null : Math.round(prior),
    { minBaseForPct: Infinity, unit: " pts", arrowOnNew: false },
  );
}

/** Ledger change for counts and durations, as a percent. */
export function percentChange(current: number | null, prior: number | null) {
  return changeText(current, prior, { minBaseForPct: 0, flatBand: 1, arrowOnNew: false });
}

function versus(pct: number | null, prev: string): string {
  if (pct === null) return `with none in ${prev}`;
  if (Math.abs(pct) < 2) return `about the same as ${prev}`;
  return pct > 0 ? `${pct}% more than ${prev}` : `${-pct}% fewer than ${prev}`;
}

/** "PxE merged 1,204 pull requests in September, 12% more than August" */
export function deliveryHeadline(r: Pick<DeliveryReport, "headline" | "compare">): string {
  const { current, previous } = r.compare;
  const c = r.headline.current;
  if (c.merged === 0) return `No PxE pull requests merged in ${current.short}`;
  return `PxE merged ${fmtInt(c.merged)} pull requests in ${current.short}, ${versus(r.headline.changePct, previous.short)}`;
}

function checkedSentence(prev: WindowStats, cur: WindowStats, prevName: string): string {
  if (cur.checkedPct === null) return "";
  const now = fmtPct(cur.checkedPct);
  if (prev.checkedPct === null) return `${now} were checked before merge.`;
  const diff = Math.round(cur.checkedPct) - Math.round(prev.checkedPct);
  if (diff === 0) return `${now} were checked before merge, the same share as ${prevName}.`;
  return `${now} were checked before merge, ${diff > 0 ? "up" : "down"} from ${fmtPct(prev.checkedPct)} in ${prevName}.`;
}

export function deliveryDek(r: Pick<DeliveryReport, "headline" | "compare">): string {
  const { previous: p, current: c } = r.headline;
  const parts = [checkedSentence(p, c, r.compare.previous.short)];
  if (c.medianHoursToFirstReview !== null && c.medianHoursToMerge !== null) {
    parts.push(
      `The median PR waited ${fmtNum(c.medianHoursToFirstReview)} hours for its first review and merged after ${fmtNum(c.medianHoursToMerge)} hours.`,
    );
  }
  return parts.filter(Boolean).join(" ");
}

/* ---------------- Pace ---------------- */

export function paceHeadline(p: DeliveryPace): string {
  const now = p.pxe.toDate.merged;
  const then = p.pxe.lastToDate.merged;
  const q = p.quarter.short;
  if (then === 0) return `${q} so far: ${fmtInt(now)} PxE pull requests in ${plural(p.quarter.days, "day")}`;
  const pct = Math.round((100 * (now - then)) / then);
  const where =
    Math.abs(pct) < 2
      ? `level with the same point in ${p.lastQuarter.short}`
      : `${Math.abs(pct)}% ${pct > 0 ? "ahead of" : "behind"} the same point in ${p.lastQuarter.short}`;
  return `${q} is ${where}`;
}

export function paceDek(p: DeliveryPace): string {
  const first = `${fmtInt(p.pxe.toDate.merged)} PxE PRs merged in the first ${plural(p.quarter.days, "day")} of ${p.quarter.short}, against ${fmtInt(p.pxe.lastToDate.merged)} by day ${p.quarter.days} of ${p.lastQuarter.short}.`;
  if (p.tooEarly || p.pxe.projected === null) return `${first} It's too early in the quarter to project.`;
  const vs = p.pxe.lastTotal > 0 ? `, against ${fmtInt(p.pxe.lastTotal)} in all of ${p.lastQuarter.short}` : "";
  return `${first} At this rate the quarter ends near ${fmtInt(p.pxe.projected)}${vs}.`;
}

/* ---------------- Findings ---------------- */

export interface Finding {
  key: string;
  text: string;
  /** The report that shows the detail. */
  to: string;
}

/** Thresholds for what counts as notable. */
export const STORY = {
  /** A repo merging this many or more with this share or less checked is a gap. */
  gapMinMerged: 20,
  gapMaxCheckedPct: 25,
  /** A repo with none last period and this many now is new. */
  newMinMerged: 20,
  /** One repo at this share of PxE PRs or more is a concentration. */
  concentrationPct: 25,
  /** Unmapped repos at this share of PxE PRs or more get a nudge. */
  unmappedPct: 15,
};

const name = (repo: string) => repo.split("/").pop() ?? repo;

function movers(r: DeliveryReport): Finding | null {
  const rows = r.byProduct.rows.filter((x) => x.change !== 0);
  if (rows.length === 0) return null;
  const up = [...rows].sort((a, b) => b.change - a.change)[0]!;
  const down = [...rows].sort((a, b) => a.change - b.change)[0]!;
  const cur = r.compare.current.short;
  const parts: string[] = [];
  if (up.change > 0) parts.push(`${up.label} added the most (${signed(up.change)}, to ${fmtInt(up.current.merged)})`);
  if (down.change < 0) parts.push(`${down.label} fell the most (${signed(down.change)}, to ${fmtInt(down.current.merged)})`);
  if (parts.length === 0) return null;
  return {
    key: "movers",
    text: `By product in ${cur}: ${joinList(parts)}. PxE overall moved ${signed(r.headline.change)}.`,
    to: "/delivery/breakdown",
  };
}

function reviewGaps(r: DeliveryReport): Finding | null {
  const gaps = r.byRepo.filter(
    (x) =>
      x.tier !== "outside_pxe" &&
      x.current.merged >= STORY.gapMinMerged &&
      (x.current.checkedPct ?? 0) <= STORY.gapMaxCheckedPct,
  );
  if (gaps.length === 0) return null;
  const merged = gaps.reduce((s, x) => s + x.current.merged, 0);
  const share = r.headline.current.merged > 0 ? (100 * merged) / r.headline.current.merged : 0;
  const list = gaps
    .slice(0, 3)
    .map((x) => `${name(x.label)} (${fmtInt(x.current.merged)}, ${fmtPct(x.current.checkedPct)} checked)`);
  const more = gaps.length > 3 ? ` and ${fmtInt(gaps.length - 3)} more` : "";
  return {
    key: "gaps",
    text: `${plural(gaps.length, "repo")} merged most PRs with no review, no second person merging and no passing CI in ${r.compare.current.short}: ${joinList(list)}${more}. Together that's ${fmtPct(share)} of PxE PRs.`,
    to: "/delivery/breakdown",
  };
}

function newRepos(r: DeliveryReport): Finding | null {
  const fresh = r.byRepo.filter((x) => x.previous.merged === 0 && x.current.merged >= STORY.newMinMerged);
  if (fresh.length === 0) return null;
  const list = fresh
    .slice(0, 3)
    .map((x) => `${name(x.label)} (${fmtInt(x.current.merged)}, ${fmtPct(x.current.checkedPct)} checked)`);
  return {
    key: "new",
    text: `New in ${r.compare.current.short}, with no PRs in ${r.compare.previous.short}: ${joinList(list)}.`,
    to: "/delivery/breakdown",
  };
}

/** How the checked PRs were checked: how much rests on review vs. other checks. */
function checkMix(r: DeliveryReport): Finding | null {
  const c = r.headline.current;
  if (c.merged === 0) return null;
  const share = (n: number) => fmtPct((100 * n) / c.merged);
  const none = c.merged - c.checked;
  return {
    key: "mix",
    text: `How PxE PRs were checked in ${r.compare.current.short}: ${share(c.viaReview)} reviewed, ${share(c.viaMerger)} merged by someone else without a review, ${share(c.viaCi)} with passing CI only, and ${share(none)} with none of the three.`,
    to: "/delivery/trends",
  };
}

function pending(r: DeliveryReport): Finding | null {
  const n = r.headline.current.checksUnknown;
  if (n === 0) return null;
  return {
    key: "pending",
    text: `Merged-by and CI are still being backfilled for ${plural(n, "PR")} in ${r.compare.current.short}, so the checked share reads low until that finishes.`,
    to: "/delivery/trends",
  };
}

function concentration(r: DeliveryReport): Finding | null {
  const total = r.headline.current.merged;
  const top = r.byRepo.find((x) => x.tier !== "outside_pxe");
  if (!top || total === 0) return null;
  const share = (100 * top.current.merged) / total;
  if (share < STORY.concentrationPct) return null;
  return {
    key: "concentration",
    text: `${name(top.label)} alone was ${fmtPct(share)} of PxE PRs in ${r.compare.current.short} (${fmtInt(top.current.merged)}).`,
    to: "/delivery/breakdown",
  };
}

function unmapped(r: DeliveryReport): Finding | null {
  const row = r.byProduct.rows.find((x) => x.key === "unmapped");
  const total = r.headline.current.merged;
  if (!row || total === 0) return null;
  const share = (100 * row.current.merged) / total;
  if (share < STORY.unmappedPct) return null;
  return {
    key: "unmapped",
    text: `${fmtPct(share)} of PxE PRs came from repos with no product, so the product view is incomplete. Map them in Admin, Products.`,
    to: "/settings/products",
  };
}

/** Review coverage across complete periods: a streak, or the range it moved in. */
export function reviewTrend(s: DeliverySeries): string | null {
  const unit = s.grain === "quarter" ? "quarters" : s.grain === "week" ? "weeks" : "months";
  const points = s.periods
    .filter((p) => !p.live && !p.partialHistory)
    .map((p) => ({ p, stats: s.points.find((x) => x.period === p.period)?.stats }))
    .filter((x): x is { p: (typeof s.periods)[number]; stats: WindowStats } => x.stats?.checkedPct != null);
  if (points.length < 3) return null;
  const pcts = points.map((x) => Math.round(x.stats.checkedPct!));
  let streak = 0;
  const dir = Math.sign(pcts[pcts.length - 1]! - pcts[pcts.length - 2]!);
  if (dir !== 0) {
    for (let i = pcts.length - 1; i > 0 && Math.sign(pcts[i]! - pcts[i - 1]!) === dir; i--) streak++;
  }
  const last = points[points.length - 1]!;
  if (streak >= 2) {
    const from = points[points.length - 1 - streak]!;
    return `The share checked before merge has ${dir > 0 ? "risen" : "fallen"} ${streak} ${unit} running, from ${fmtPct(from.stats.checkedPct)} in ${from.p.short} to ${fmtPct(last.stats.checkedPct)} in ${last.p.short}.`;
  }
  const recent = points.slice(-6);
  const lo = recent.reduce((a, b) => (b.stats.checkedPct! < a.stats.checkedPct! ? b : a));
  const hi = recent.reduce((a, b) => (b.stats.checkedPct! > a.stats.checkedPct! ? b : a));
  return `Over the last ${recent.length} ${unit}, the share checked before merge ranged from ${fmtPct(lo.stats.checkedPct)} (${lo.p.short}) to ${fmtPct(hi.stats.checkedPct)} (${hi.p.short}).`;
}

export function findings(r: DeliveryReport, series?: DeliverySeries): Finding[] {
  const trend = series ? reviewTrend(series) : null;
  const out = [
    pending(r),
    checkMix(r),
    movers(r),
    trend ? { key: "trend", text: trend, to: "/delivery/trends" } : null,
    reviewGaps(r),
    newRepos(r),
    concentration(r),
    unmapped(r),
  ];
  if (r.compare.previous.partialHistory) {
    out.push({
      key: "history",
      text: `Aura's history starts partway through ${r.compare.previous.short} for at least one org, so ${r.compare.previous.short} undercounts and the change reads high.`,
      to: "/delivery/trends",
    });
  }
  return out.filter((f): f is Finding => f !== null);
}
